import { useEffect, useState } from "react";
import {
  enableAdminPush,
  getPushState,
  type PushState,
} from "@/lib/push";

type IosPwaGuideProps = {
  isAuthenticated: boolean;
};

type GuideMode = "install" | "notifications" | null;

const INSTALL_DISMISSED_KEY = "hp-ios-pwa-install-dismissed";
const PUSH_DISMISSED_KEY = "hp-ios-pwa-push-dismissed";

function isSessionDismissed(key: string): boolean {
  try {
    return window.sessionStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function markSessionDismissed(key: string) {
  try {
    window.sessionStorage.setItem(key, "1");
  } catch {
    // Session storage can be unavailable in strict browser settings.
  }
}

function isIosDevice(): boolean {
  const ua = window.navigator.userAgent;
  const platform = window.navigator.platform;
  return /iPhone|iPad|iPod/.test(ua) ||
    (platform === "MacIntel" && window.navigator.maxTouchPoints > 1);
}

function isStandalonePwa(): boolean {
  return window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
}

function pushCopy(state: PushState): { title: string; body: string; action: string } {
  if (state === "denied") {
    return {
      title: "Meldingen staan uit",
      body: "Open iPhone Instellingen > Meldingen > Haringparty Admin en zet meldingen aan.",
      action: "Begrepen",
    };
  }

  if (state === "unsupported") {
    return {
      title: "Update iOS voor meldingen",
      body: "Web push werkt op iPhone alleen in de geinstalleerde app vanaf iOS 16.4.",
      action: "Begrepen",
    };
  }

  return {
    title: "Zet meldingen aan",
    body: "Krijg direct bericht bij nieuwe bestellingen en betaalstatuswijzigingen.",
    action: "Meldingen aanzetten",
  };
}

export function IosPwaGuide({ isAuthenticated }: IosPwaGuideProps) {
  const [mode, setMode] = useState<GuideMode>(null);
  const [pushState, setPushState] = useState<PushState>("default");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isIosDevice()) return;

    if (!isStandalonePwa()) {
      if (!isSessionDismissed(INSTALL_DISMISSED_KEY)) {
        setMode("install");
      }
      return;
    }

    if (!isAuthenticated) return;

    getPushState()
      .then((state) => {
        setPushState(state);
        if (
          state !== "enabled" &&
          !isSessionDismissed(PUSH_DISMISSED_KEY)
        ) {
          setMode("notifications");
        }
      })
      .catch(() => {
        setPushState("unsupported");
        if (!isSessionDismissed(PUSH_DISMISSED_KEY)) {
          setMode("notifications");
        }
      });
  }, [isAuthenticated]);

  function dismissInstall() {
    markSessionDismissed(INSTALL_DISMISSED_KEY);
    setMode(null);
  }

  function dismissPush() {
    markSessionDismissed(PUSH_DISMISSED_KEY);
    setMode(null);
  }

  async function enablePush() {
    if (pushState === "denied" || pushState === "unsupported") {
      dismissPush();
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const next = await enableAdminPush();
      setPushState(next);
      if (next === "enabled") {
        markSessionDismissed(PUSH_DISMISSED_KEY);
        setMode(null);
      } else if (next === "denied") {
        setError("Meldingen zijn geblokkeerd in iOS.");
      } else {
        setError("Meldingen zijn nog niet aangezet.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Meldingen aanzetten mislukt");
      setPushState(await getPushState().catch((): PushState => "unsupported"));
    } finally {
      setBusy(false);
    }
  }

  if (mode === "install") {
    return (
      <div className="ios-pwa-screen" role="dialog" aria-modal="true" aria-labelledby="ios-pwa-title">
        <div className="ios-pwa-card ios-pwa-install">
          <div className="ios-pwa-logo-wrap">
            <img src="/logo.png" alt="" />
          </div>
          <p className="ios-pwa-kicker">iPhone setup</p>
          <h2 id="ios-pwa-title">Zet de admin op je beginscherm</h2>
          <p className="ios-pwa-intro">
            Zo opent het admin dashboard als app en kunnen bestelmeldingen straks direct binnenkomen.
          </p>

          <div className="ios-safari-preview" aria-hidden="true">
            <div className="ios-url">admin.odeaandeharing.nl</div>
            <div className="ios-share-icon">↑</div>
          </div>

          <ol className="ios-pwa-steps">
            <li><span>1</span><p>Tik onderin Safari op Deel.</p></li>
            <li><span>2</span><p>Kies Zet op beginscherm.</p></li>
            <li><span>3</span><p>Open Haringparty Admin vanaf je beginscherm.</p></li>
            <li><span>4</span><p>Log in en zet Push aan voor meldingen.</p></li>
          </ol>

          <button type="button" className="ios-pwa-secondary" onClick={dismissInstall}>
            Verder in browser
          </button>
        </div>
      </div>
    );
  }

  if (mode === "notifications") {
    const copy = pushCopy(pushState);
    return (
      <div className="ios-pwa-screen" role="dialog" aria-modal="true" aria-labelledby="ios-push-title">
        <div className="ios-pwa-card ios-pwa-push">
          <div className="ios-pwa-bell" aria-hidden="true">
            <span />
          </div>
          <p className="ios-pwa-kicker">Laat niets missen</p>
          <h2 id="ios-push-title">{copy.title}</h2>
          <p className="ios-pwa-intro">{copy.body}</p>
          {error && <div className="ios-pwa-error">{error}</div>}
          <div className="ios-pwa-actions">
            <button
              type="button"
              className="ios-pwa-primary"
              onClick={enablePush}
              disabled={busy}
            >
              {busy ? "Even wachten..." : copy.action}
            </button>
            <button type="button" className="ios-pwa-secondary" onClick={dismissPush}>
              Later
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
